import React, { ComponentType, FC, MutableRefObject, ReactNode, useMemo, useRef } from "react";

import { useDimensions } from "@keepeek/commons";
import {
  ComponentNextLinkProps,
  KMosaicAnimationType,
  KMosaicElement,
  KMosaicElementSize,
  KMosaicVerticalTextAlignType,
  KpkMosaicElement,
  KpkMosaicLabelCustomElement,
} from "@keepeek/refront-components";
import { Box, Grid } from "@mui/material";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import { animated, useSpring } from "react-spring";

import { KIcon } from "../../../components/common/KIcon";
import { KWidgetProps } from "../models";

const PREFIX = "KCarouselWidget";

const classes = {
  root: `${PREFIX}-root`,
  list: `${PREFIX}-list`,
  item: `${PREFIX}-item`,
  detail: `${PREFIX}-detail`,
  overflowLeft: `${PREFIX}-overflowLeft`,
  overflowRight: `${PREFIX}-overflowRight`,
};

const StyledKCarouselWidget = styled("div", {
  shouldForwardProp: (prop) => prop !== "verticalTextAlign" && prop !== "animationType",
})<{
  animationType?: KMosaicAnimationType;
  verticalTextAlign?: KMosaicVerticalTextAlignType | undefined;
}>(({ theme, animationType, verticalTextAlign }) => ({
  [`&.${classes.root}`]: {
    display: "block",
    overflow: "hidden",
    position: "relative",
  },

  [`& .${classes.list}`]: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    flexWrap: "nowrap",
    maxWidth: "100%",
    overflowX: "auto",
    paddingBottom:
      verticalTextAlign === KMosaicVerticalTextAlignType.UnderMosaic
        ? theme.spacing(1)
        : theme.spacing(2),
    "& .MuiGrid-item": {
      padding: theme.spacing(1),
    },
  },

  [`& .${classes.item}`]: {
    marginRight: theme.spacing(1),
    flexShrink: 0,
  },

  [`& .${classes.detail}`]: {},

  [`& .${classes.overflowLeft}`]: {
    position: "absolute",
    background: "linear-gradient(90deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)",
    width: theme.spacing(5),
    height: "100%",
    top: 0,
    left: 0,
    zIndex: 1,
    pointerEvents: "none",
  },

  [`& .${classes.overflowRight}`]: {
    position: "absolute",
    background: "linear-gradient(270deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)",
    width: theme.spacing(5),
    height: "100%",
    top: 0,
    right: 0,
    zIndex: 1,
    pointerEvents: "none",
  },
  ...(animationType &&
    animationType === KMosaicAnimationType.ZoomOnHover && {
      "&  .KMosaicElement-media": {
        transition: "all 250ms ease-out",
        backgroundSize: "160%",
        "&:hover": {
          backgroundSize: "170%",
        },
      },
      "& .KMosaicElement-action": {
        overflow: "hidden",
      },
      "& .KMosaicElement-media img": {
        transition: "all 250ms ease-out",
        scale: "1",
        "&:hover": {
          scale: "1.1",
        },
      },
    }),
}));

export type KCarouselWidgetProps = {
  elements: KpkMosaicElement[];
  currentSelectedElement: KpkMosaicElement | null;
  clickDuringLoadingElement?: KpkMosaicElement;
  onElementClick: (element: KpkMosaicElement) => void;
  elementDetail: ReactNode;
  elementSize?: KMosaicElementSize;
  labelCustom?: KpkMosaicLabelCustomElement;
  animationType?: KMosaicAnimationType;
  ComponentNextLink?: ComponentType<ComponentNextLinkProps>;
  borderRadius?: string;
} & KWidgetProps;

const KCarouselWidget: FC<React.PropsWithChildren<KCarouselWidgetProps>> = function ({
  elements,
  currentSelectedElement,
  clickDuringLoadingElement,
  elementDetail,
  elementSize = KMosaicElementSize.M,
  onElementClick,
  labelCustom,
  animationType,
  ComponentNextLink,
  borderRadius,
}) {
  const { ref, dimensions, node } = useDimensions({ ownContext: true });
  const canScroll: boolean = (dimensions?.scrollWidth ?? 0) > (dimensions?.width ?? 0);
  const mosaicRef = useRef<any>(null);

  const overflowLeft: boolean = canScroll && (dimensions?.scrollLeft ?? 0) > 0;
  const overflowRight: boolean =
    canScroll &&
    (dimensions?.scrollWidth ?? 0) - (dimensions?.scrollLeft ?? 0) > (dimensions?.width ?? 0);

  const leftOverflow = useSpring({ opacity: overflowLeft ? 1 : 0 });
  const rightOverflow = useSpring({ opacity: overflowRight ? 1 : 0 });

  const handleClick = (element: KpkMosaicElement) => {
    const index = elements.findIndex((el) => el.id === element.id);
    const scrollPosition = ((dimensions?.scrollWidth ?? 0) / elements.length) * index;

    node?.scrollTo({ left: scrollPosition, behavior: "smooth" });
    onElementClick(element);
  };

  const { maxHeaderHeight } = useMosaicHeaderHeight({ labelCustom, ref: mosaicRef });

  return (
    <StyledKCarouselWidget
      className={clsx(
        classes.root,
        "KCarousel",
        canScroll && "KCarousel-canScroll",
        overflowLeft && "KCarousel-overflowLeft",
        overflowRight && "KCarousel-overflowRight",
      )}
      animationType={animationType}
      verticalTextAlign={labelCustom?.verticalTextAlign}
      ref={mosaicRef}
    >
      {/* For overflow design only */}
      <animated.span
        style={{ ...leftOverflow, height: dimensions.height }}
        className={classes.overflowLeft}
      />
      <Grid
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        ref={ref}
        container
        spacing={2}
        component="ol"
        className={classes.list}
      >
        {elements.map((element) => (
          <KMosaicElement
            key={element.id}
            selected={currentSelectedElement?.id === element.id}
            className={classes.item}
            element={element}
            onElementClick={handleClick}
            elementSize={elementSize}
            loading={clickDuringLoadingElement?.id === element.id}
            labelCustom={labelCustom}
            ComponentNextLink={ComponentNextLink}
            borderRadius={borderRadius}
            maxHeaderHeight={maxHeaderHeight}
            KIcon={KIcon}
          />
        ))}
      </Grid>
      {/* For overflow design only */}
      <animated.span
        style={{ ...rightOverflow, height: dimensions.height }}
        className={classes.overflowRight}
      />
      {!!currentSelectedElement && currentSelectedElement.hasChildren && !!elementDetail && (
        <Box className={classes.detail}>{elementDetail}</Box>
      )}
    </StyledKCarouselWidget>
  );
};

type MosaicHeaderHeightProps = {
  labelCustom: KpkMosaicLabelCustomElement | undefined;
  ref: MutableRefObject<any>;
};

export function useMosaicHeaderHeight({ labelCustom, ref }: MosaicHeaderHeightProps) {
  /**
   * Calculate the maximum height of headers in a mosaic
   * Used when the mosaic has its titles under the mosaic element
   */
  const getMaxHeaderHeight = useMemo(() => {
    if (
      !ref.current ||
      labelCustom?.verticalTextAlign !== KMosaicVerticalTextAlignType.UnderMosaic
    ) {
      return -1;
    }

    const mosaicElementHeaders = [...ref.current.querySelectorAll(".KMosaicElement-header")];
    const heights = mosaicElementHeaders.map((el) => el.clientHeight);
    return Math.max(...heights);
  }, [ref, labelCustom]);

  return {
    maxHeaderHeight: getMaxHeaderHeight,
  };
}

export default KCarouselWidget;
